import { useState, useEffect } from "react";
import { fetchGiftListForApply, applyGift, cancelGift } from "../api/giftApi";
import { getCodeList } from "../api/codeApi";
import Toast from "../components/Toast";

export default function GiftApplyPage({ onLogout }) {
  const [list,       setList]       = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [toast,      setToast]      = useState('');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [yearList, setYearList] = useState([]);
  const [selected,   setSelected]   = useState({}); // { seq_no: opt_no }

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const loadList = async (year) => {
    setLoading(true);
    try {
      const res = await fetchGiftListForApply(year);
      if (res.success) {
        setList(res.data);
        // 기존 신청 정보 초기화
        const init = {};
        res.data.forEach(g => { if (g.my_opt_no) init[g.seq_no] = g.my_opt_no; });
        setSelected(init);
      } else if (res.message === '토큰이 만료되었습니다. 다시 로그인해주세요.') onLogout();
    } catch (err) { showToast('❌ 목록 조회 실패'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadList(yearFilter); }, [yearFilter]);
  useEffect(() => {
    const codes = getCodeList('YEAR_LIST');
    setYearList(codes);
    if (codes.length > 0 && !codes.find(c => c.code === new Date().getFullYear().toString())) {
      setYearFilter(codes[0].code);
    }
  }, []);

  const handleApply = async (gift) => {
    const opt_no = selected[gift.seq_no];
    if (!opt_no) { showToast('❌ 옵션을 선택해주세요.'); return; }
    try {
      const res = await applyGift(gift.gift_year, gift.seq_no, opt_no);
      if (res.success) { showToast('✅ 신청이 완료되었습니다.'); loadList(yearFilter); }
      else showToast(`❌ ${res.message}`);
    } catch (err) { showToast('❌ 신청 실패'); }
  };

  const handleCancel = async (gift) => {
    try {
      const res = await cancelGift(gift.gift_year, gift.seq_no);
      if (res.success) { showToast('✅ 신청이 취소되었습니다.'); loadList(yearFilter); }
      else showToast(`❌ ${res.message}`);
    } catch (err) { showToast('❌ 취소 실패'); }
  };

  return (
    <div style={styles.wrap}>
      <Toast msg={toast} />

      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>🎁 선물 신청</h1>
        <select style={styles.select} value={yearFilter}
          onChange={e => setYearFilter(e.target.value)}>
          {yearList.map(y => (
            <option key={y.code} value={y.code}>{y.code_nm}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div style={styles.empty}>⏳ 불러오는 중...</div>
      ) : list.length === 0 ? (
        <div style={styles.empty}>신청 가능한 선물이 없습니다.</div>
      ) : (
        <div style={styles.cardGrid}>
          {list.map((gift, i) => {
            const isApplied = !!gift.my_opt_no;
            return (
              <div key={i} style={{ ...styles.giftCard, border: isApplied ? "2px solid #1677ff" : "2px solid #e0e0e0" }}>
                {/* 신청완료 배지 */}
                {isApplied && (
                  <div style={styles.appliedBadge}>✅ 신청완료</div>
                )}

                {/* 이미지 */}
                <div style={styles.imgWrap}>
                  {gift.img_data
                    ? <img src={gift.img_data} alt={gift.gift_name} style={styles.img} />
                    : <div style={styles.imgPlaceholder}>🎁</div>
                  }
                </div>

                {/* 선물명 */}
                <div style={styles.giftName}>{gift.gift_name}</div>

                {/* 옵션 선택 */}
                <div style={styles.optionWrap}>
                  {gift.options?.map((opt, j) => (
                    <label key={j} style={{
                      ...styles.optionLabel,
                      background: selected[gift.seq_no] === opt.opt_no ? "#e8f4fd" : "#f5f5f5",
                      border: selected[gift.seq_no] === opt.opt_no ? "1.5px solid #1677ff" : "1.5px solid #e0e0e0",
                      color: selected[gift.seq_no] === opt.opt_no ? "#1677ff" : "#444",
                    }}>
                      <input
                        type="radio"
                        name={`gift_${gift.seq_no}`}
                        value={opt.opt_no}
                        checked={selected[gift.seq_no] === opt.opt_no}
                        onChange={() => setSelected(p => ({ ...p, [gift.seq_no]: opt.opt_no }))}
                        style={{ marginRight: 6 }}
                      />
                      {opt.opt_name}
                    </label>
                  ))}
                </div>

                {/* 버튼 */}
                <div style={styles.btnWrap}>
                  {isApplied ? (
                    <>
                      <div style={{ fontSize: 12, color: "#1677ff", textAlign: "center", marginBottom: 8 }}>
                        선택: {gift.options?.find(o => o.opt_no === gift.my_opt_no)?.opt_name || '-'}
                      </div>
                      <button style={styles.cancelBtn} onClick={() => handleCancel(gift)}>
                        신청 취소
                      </button>
                      <button style={styles.applyBtn} onClick={() => handleApply(gift)}>
                        옵션 변경
                      </button>
                    </>
                  ) : (
                    <button style={styles.applyBtn} onClick={() => handleApply(gift)}>
                      신청하기
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles = {
  wrap        : { background: "#f5f7fa", padding: 24, fontFamily: "'Noto Sans KR', sans-serif" },
  pageHeader  : { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
  pageTitle   : { fontSize: 22, fontWeight: 800, color: "#1a1a2e", margin: 0 },
  select      : { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "8px 12px", fontSize: 14, outline: "none", background: "#fff", cursor: "pointer" },
  empty       : { textAlign: "center", padding: "80px 0", color: "#bbb", fontSize: 15 },
  cardGrid    : { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 },
  giftCard    : { background: "#fff", borderRadius: 16, padding: 20, boxShadow: "0 4px 16px rgba(0,0,0,0.08)", position: "relative", display: "flex", flexDirection: "column", gap: 12 },
  appliedBadge: { position: "absolute", top: 12, right: 12, background: "#1677ff", color: "#fff", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20 },
  imgWrap     : { width: "100%", aspectRatio: "4/3", borderRadius: 10, overflow: "hidden", background: "#f5f7fa", display: "flex", alignItems: "center", justifyContent: "center" },
  img         : { width: "100%", height: "100%", objectFit: "contain" },  // ← cover → contain
  imgPlaceholder: { fontSize: 60 },
  giftName    : { fontSize: 16, fontWeight: 700, color: "#1a1a2e", textAlign: "center" },
  optionWrap  : { display: "flex", flexDirection: "column", gap: 6 },
  optionLabel : { display: "flex", alignItems: "center", padding: "8px 12px", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 500, transition: "all 0.15s" },
  btnWrap     : { display: "flex", flexDirection: "column", gap: 6, marginTop: 4 },
  applyBtn    : { background: "linear-gradient(135deg, #1677ff, #4096ff)", color: "#fff", border: "none", borderRadius: 8, padding: "12px", fontSize: 14, fontWeight: 700, cursor: "pointer" },
  cancelBtn   : { background: "#fff", border: "1.5px solid #ff4d4f", color: "#ff4d4f", borderRadius: 8, padding: "10px", fontSize: 14, fontWeight: 600, cursor: "pointer" },
};